import React, { useState } from "react";
import MarkdownWrapper from "../MarkdownWrapper";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import CloseIcon from "@material-ui/icons/Close";
import ListIcon from "@material-ui/icons/List";

const useStyles = makeStyles((theme) => ({
  text: {
    marginBottom: 6,
  },
  buttonMessageHeader: {
    fontWeight: 600,
    fontSize: 15,
    marginBottom: 4,
    lineHeight: 1.4,
  },
  buttonMessageBody: {
    fontSize: 14,
    lineHeight: 1.45,
    color: theme.palette.text.primary,
    marginBottom: 2,
  },
  buttonsWrap: {
    marginTop: 12,
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    width: "100%",
  },
  buttonChip: {
    flex: "1 1 0",
    minWidth: 80,
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "#E8F4F0",
    color: theme.palette.mode === "dark" ? "#7FDBDA" : "#008069",
    border: "none",
    borderRadius: 8,
    padding: "10px 16px",
    fontSize: 14,
    fontWeight: 500,
    cursor: "default",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "center",
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  },
  listButtonWrap: {
    marginTop: 8,
  },
  listButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#008069",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 16px",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#006655",
    },
  },
  listWrap: {
    marginTop: 8,
    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.12)"}`,
    borderRadius: 8,
    overflow: "hidden",
  },
  listRow: {
    padding: "10px 12px",
    fontSize: 13,
    borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: 8,
  },
  modalSection: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  modalSectionTitle: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 12,
    paddingBottom: 4,
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
  },
  modalRow: {
    paddingTop: 12,
    paddingBottom: 12,
  },
}));

/**
 * Parse body saved by backend for buttonsMessage: [BUTTON]\n\n*text*\n\n*id* - title\n...
 */
function parseButtonBody(body) {
  if (!body || typeof body !== "string" || !body.startsWith("[BUTTON]")) {
    return { text: "", buttons: [] };
  }
  const rest = body.replace(/^\[BUTTON\]\n\n/, "");
  const parts = rest.split(/\n\n+/);
  const text = (parts[0] || "").replace(/\*/g, "").trim();
  const buttons = [];
  for (let i = 1; i < parts.length; i++) {
    const line = parts[i].trim();
    const match = line.match(/^\*(.+?)\*\s*-\s*(.+)$/);
    if (match) {
      buttons.push({ id: match[1], title: match[2] });
    } else if (line) {
      buttons.push({ id: String(i), title: line.replace(/\*/g, "").trim() });
    }
  }
  return { text, buttons };
}

/**
 * Parse body saved by backend for listMessage: [LIST]\n\n*description*\n\ntitle1\ntitle2\n...
 */
function parseListBody(body) {
  if (!body || typeof body !== "string" || !body.startsWith("[LIST]")) {
    return { text: "", rows: [] };
  }
  const rest = body.replace(/^\[LIST\]\n\n/, "").trim();
  const firstDouble = rest.indexOf("\n\n");
  const textBlock = firstDouble >= 0 ? rest.slice(0, firstDouble) : rest;
  const linesBlock = firstDouble >= 0 ? rest.slice(firstDouble + 2) : "";
  const text = textBlock.replace(/\*/g, "").trim();
  const rows = linesBlock
    .split("\n")
    .map((line) => line.replace(/\*/g, "").trim())
    .filter(Boolean)
    .map((title, i) => ({ id: String(i + 1), title }));
  return { text, rows };
}

export function InteractiveMessagePreview({ message }) {
  const classes = useStyles();
  const [listModalOpen, setListModalOpen] = useState(false);
  const { mediaType, body } = message || {};

  if (mediaType === "buttonsMessage") {
    const { text, buttons } = parseButtonBody(body);
    const lines = text ? text.split("\n").map((l) => l.trim()).filter(Boolean) : [];
    const hasMultipleLines = lines.length > 1;
    const headerText = hasMultipleLines ? lines[0] : null;
    const bodyText = hasMultipleLines ? lines.slice(1).join("\n") : (lines[0] || text || "");
    return (
      <>
        {text && (
          <div className={classes.text}>
            {headerText && (
              <div className={classes.buttonMessageHeader}>
                <MarkdownWrapper>{headerText}</MarkdownWrapper>
              </div>
            )}
            {(bodyText || (!headerText && text)) && (
              <div className={classes.buttonMessageBody}>
                <MarkdownWrapper>{bodyText || text}</MarkdownWrapper>
              </div>
            )}
          </div>
        )}
        {buttons.length > 0 && (
          <div className={classes.buttonsWrap}>
            {buttons.map((btn, idx) => (
              <span key={idx} className={classes.buttonChip}>
                {btn.title}
              </span>
            ))}
          </div>
        )}
      </>
    );
  }

  if (mediaType === "listMessage" || mediaType === "interactiveMessage") {
    const { text, rows } = parseListBody(body);
    return (
      <>
        {text && (
          <div className={classes.text}>
            <MarkdownWrapper>{text}</MarkdownWrapper>
          </div>
        )}
        {rows.length > 0 && (
          <div className={classes.listButtonWrap}>
            <button
              type="button"
              className={classes.listButton}
              onClick={() => setListModalOpen(true)}
              aria-label="Escolha uma opção"
            >
              <ListIcon style={{ fontSize: 20 }} />
              Escolha uma opção
            </button>
            <Dialog
              open={listModalOpen}
              onClose={() => setListModalOpen(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{ style: { borderRadius: 12 } }}
            >
              <DialogTitle disableTypography className={classes.dialogTitle}>
                <span style={{ fontSize: 18, fontWeight: 600 }}>Escolha uma opção</span>
                <IconButton size="small" onClick={() => setListModalOpen(false)} aria-label="Fechar">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <div className={classes.modalSectionTitle}>Opções</div>
              <List dense>
                {rows.map((row, idx) => (
                  <ListItem key={idx} className={classes.modalRow} divider={idx < rows.length - 1}>
                    <ListItemText primary={row.title} />
                  </ListItem>
                ))}
              </List>
            </Dialog>
          </div>
        )}
      </>
    );
  }

  return null;
}

export default InteractiveMessagePreview;
